import {MutableRefObject, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {
  EditPaymentRequestBody,
  OrderPaymentResponseBody,
  useEditPurchasePaymentMutation,
  useEditSaleBalancePaymentMutation,
  useEditSaleDepositPaymentMutation,
} from '@dms/api/sales';
import {handleApiError} from '@dms/shared';

import {ApiError, isNilOrZero} from 'shared/web';

import {FormValuesType} from '../types/FormValuesType';

interface UseUpdateForeignCurrencyExchangeRateProps {
  payment: OrderPaymentResponseBody;
  orderId: string;
  businessCaseId: string;
  getPaymentBody: (data: FormValuesType) => EditPaymentRequestBody;
  editPurchasePayment: ReturnType<typeof useEditPurchasePaymentMutation>[0];
  editSaleDepositPayment: ReturnType<typeof useEditSaleDepositPaymentMutation>[0];
  editSaleBalancePayment: ReturnType<typeof useEditSaleBalancePaymentMutation>[0];
}

interface UseUpdateForeignCurrencyExchangeRateReturn {
  updateExchangeRate: (formApi: UseFormReturn<FormValuesType>) => void;
  markExchangeRateEditPending: () => void;
  hasUnsentExchangeRateEditRef: MutableRefObject<boolean>;
  latestDispatchedRequestIdRef: MutableRefObject<number>;
  latestSettledRequestIdRef: MutableRefObject<number>;
  staleSettledExchangeRateRef: MutableRefObject<string | null>;
}

/**
 * Shared by every trigger that edits a payment and can affect its exchangeRate/exchangeRateAmount
 * (the exchangeRate field's own onChange, and the amount/percentage fields' onChange) so they all
 * guard against the same three sources of stale writes into those fields: an in-flight request's own
 * response, the cache refetch its invalidatesTags triggers, and the default-fill effect (in
 * ForeignCurrencyFields) reacting to that same refetch.
 *
 * Three trackers cover this:
 * - hasUnsentExchangeRateEditRef signals to the default-fill effect that this call hasn't finished
 *   deciding what to do with the current edit yet. It's cleared as soon as that decision is made —
 *   whether that's dispatching a request or determining no request is needed (e.g. the field was
 *   cleared) — so the effect isn't blocked forever once the edit is resolved.
 * - editVersionRef is a monotonic counter bumped on every edit; a request's own callback compares it
 *   against the version captured when that request was dispatched, so it can tell whether a *newer*
 *   edit has happened since — dispatched or not — and skip applying its (now stale) response.
 * - staleSettledExchangeRateRef records the value carried by a response that arrived after a newer
 *   edit already moved past it (e.g. the field was cleared while that request was still in flight).
 *   The request's own callback is already guarded by editVersionRef above, but the cache refetch its
 *   invalidatesTags triggers isn't — it would otherwise reach the default-fill effect with that same
 *   stale value and undo the newer edit. The effect skips applying payment.exchangeRate exactly when
 *   it still matches this recorded value, and resumes applying it as soon as a genuinely new value
 *   (e.g. from an actual currency change) comes in.
 *
 * latestDispatchedRequestIdRef/latestSettledRequestIdRef cover a newer request that has already been
 * dispatched but not yet resolved, so an older, slower response can't win either.
 *
 * `formApi` is a call-time argument (not a hook input) because callers can be outside the
 * react-hook-form `<Form>` render prop where `formApi` lives.
 */
export const useUpdateForeignCurrencyExchangeRate = (
  props: UseUpdateForeignCurrencyExchangeRateProps
): UseUpdateForeignCurrencyExchangeRateReturn => {
  const hasUnsentExchangeRateEditRef = useRef(false);
  const editVersionRef = useRef(0);
  const latestDispatchedRequestIdRef = useRef(0);
  const latestSettledRequestIdRef = useRef(0);
  const staleSettledExchangeRateRef = useRef<string | null>(null);

  const markExchangeRateEditPending = () => {
    hasUnsentExchangeRateEditRef.current = true;
    editVersionRef.current += 1;
  };

  const updateExchangeRate = (formApi: UseFormReturn<FormValuesType>) => {
    const data = formApi.getValues();
    const editPaymentRequestBody = props.getPaymentBody(data);
    const editVersionAtCall = editVersionRef.current;

    if (isNil(props.payment.exchangeRate)) {
      hasUnsentExchangeRateEditRef.current = false;
      return;
    }
    if (isNilOrZero(data.amount) || isNilOrZero(parseFloat(data.exchangeRate ?? '0'))) {
      hasUnsentExchangeRateEditRef.current = false;
      formApi.setValue('exchangeRateAmount', '0');
      return;
    }

    hasUnsentExchangeRateEditRef.current = false;

    const editPaymentMutation =
      props.payment.paymentDiscriminator === 'PURCHASE' // eslint-disable-line no-nested-ternary -- Pre-existing violation
        ? props.editPurchasePayment
        : props.payment.paymentDiscriminator === 'DEPOSIT'
          ? props.editSaleDepositPayment
          : props.editSaleBalancePayment;

    const requestId = ++latestDispatchedRequestIdRef.current;

    editPaymentMutation({
      businessCaseId: props.businessCaseId,
      orderId: props.orderId,
      paymentId: props.payment.id,
      editPaymentRequestBody,
    })
      .unwrap()
      .then((data) => {
        const isSupersededByNewerRequest = requestId !== latestDispatchedRequestIdRef.current;

        if (isSupersededByNewerRequest) {
          return;
        }
        latestSettledRequestIdRef.current = requestId;

        const isSupersededByNewerEdit = editVersionRef.current !== editVersionAtCall;
        const payment = data.payments.find((item) => item.id === props.payment.id);

        if (isSupersededByNewerEdit) {
          staleSettledExchangeRateRef.current = payment?.exchangeRate ?? null;
          return;
        }

        formApi.setValue('amount', parseFloat(payment?.amount?.amount ?? '0'));
        formApi.setValue('exchangeRate', payment?.exchangeRate || null);
        formApi.setValue('exchangeRateAmount', payment?.foreignCurrencyAmount?.amount || null);
      })
      .catch((error: ApiError) => {
        const isSupersededByNewerRequest = requestId !== latestDispatchedRequestIdRef.current;

        if (isSupersededByNewerRequest) {
          return;
        }
        latestSettledRequestIdRef.current = requestId;

        handleApiError(error);

        const isSupersededByNewerEdit = editVersionRef.current !== editVersionAtCall;

        if (isSupersededByNewerEdit) {
          return;
        }

        formApi.setValue('exchangeRate', props.payment.exchangeRate);
        formApi.setValue('exchangeRateAmount', props.payment.foreignCurrencyAmount?.amount || null);
      });
  };

  return {
    updateExchangeRate,
    markExchangeRateEditPending,
    hasUnsentExchangeRateEditRef,
    latestDispatchedRequestIdRef,
    latestSettledRequestIdRef,
    staleSettledExchangeRateRef,
  };
};
